import { Injectable } from '@angular/core';

import { CmsComponentNodeData, CmsComponentNodeDataAttributes } from '@interfaces/cms-component-node-data';

import { CmsComponentConfig } from '@interfaces/config';
import { LegacyMediaDataUrls, MediaDataUrls, TImageConfig, TMediaNodeType } from '@interfaces/index';

import {
  FileFileData,
  ImageMetadata,
  MediaImageData,
  ParagraphCtaData,
  ParagraphTextMediaBoxData,
  ThumbnailData,
} from '@generated/content';

import { GlobalSpsErrorHandler } from '@lib/utils/global-error-handler';
import { HyphenatePipe } from '../../../pipes/hyphenate/hyphenate.pipe';

import { ButtonTypeEnum } from '@models/enums';
import { SPS_COLORS } from '@core/config';

@Injectable({ providedIn: 'root' })
export abstract class BaseComponentResolver {
  static instance: BaseComponentResolver;

  abstract resolveComponentConfig(
    node: CmsComponentNodeData,
    nodePageData: CmsComponentNodeData[]
  ): CmsComponentConfig<any> | null;

  protected nodePageData: CmsComponentNodeData[];

  /*
   * Tries to find a specific node for a given id and type and returns
   */
  static getNode<T extends CmsComponentNodeData>(
    id: string = null,
    type: string = null,
    nodePageData: CmsComponentNodeData[] = []
  ): T | null {
    if (!(id && type)) {
      GlobalSpsErrorHandler.log(`Could not find node for id '${id}' and type '${type}'.`);
      return null;
    }


    return nodePageData.find(node => node.type === type && node.id === id) as T;
  }

  static getNodeId(src: { id: string } | string): string {
    const id = typeof src === 'string' ? src : src?.id ?? Math.random();
    return `node-${id}`;
  }

  /*
   * Tries to find a Media node for a given id and type and returns
   * its available sources
   */
  static getImageConfig(
    mediaNodeId: string,
    mediaNodeType: TMediaNodeType,
    componentNodeData: CmsComponentNodeData[] = [],
    includeMetas: boolean = true
  ): TImageConfig {
    const mediaNode = this.getNode<MediaImageData>(mediaNodeId, mediaNodeType, componentNodeData);

    if (!mediaNode) {
      return null;
    }

    let id: string;
    let type: ThumbnailData.TypeEnum;
  


    if ('field_media_image' in mediaNode.relationships) {
      id = mediaNode.relationships.field_media_image.data.id;
      type = mediaNode.relationships.field_media_image.data.type;
    } else if ('thumbnail' in mediaNode.relationships) {
      id = mediaNode.relationships.thumbnail.data.id;
      type = mediaNode.relationships.thumbnail.data.type;
    }



    let dataUrls: LegacyMediaDataUrls | MediaDataUrls | null;
    let metas: ImageMetadata | null;

    const fileNode = this.getNode<FileFileData>(id, type, componentNodeData);


    if (fileNode?.attributes?.filemime === 'image/svg+xml') {
      return { dataUrls: { svg: fileNode?.attributes?.uri.url } as LegacyMediaDataUrls, metas: null };
    }

    dataUrls = fileNode?.attributes?.image_style_uri as LegacyMediaDataUrls | MediaDataUrls;

    if (includeMetas) {
      metas = mediaNode.relationships.field_media_image?.data?.meta;
    }
    let defaultImageUrl = fileNode?.attributes?.uri?.url;


    return { dataUrls: { ...dataUrls, legacy: !Object.keys(dataUrls).includes('wide_1920w_webp_21_9'),default:defaultImageUrl }, metas };
  }

  static hyphenate(string: string): string {
    return HyphenatePipe.transform(string);
  }

  /*
   * Calls individual resolver's resolve method and adds style configs
   */
  public buildComponentConfig<T>(node: CmsComponentNodeData, componentNodeData: CmsComponentNodeData[]): CmsComponentConfig<T> {
    const componentConfig = this.resolveComponentConfig(node, componentNodeData);

    if (!componentConfig?.config) {
      return componentConfig;
    }

    componentConfig.config.bgColor = this.getBgColor(node.attributes);
    componentConfig.config.margins = this.getMargins(node.attributes);
    componentConfig.config.anchor = this.getAnchor(node.attributes);
    componentConfig.config.nodeId = `node-${node.id}`;

    return componentConfig;
  }

  protected getNode<T extends CmsComponentNodeData>(id: string, type: string): T | null {
    return BaseComponentResolver.getNode<T>(id, type, this.nodePageData);
  }

  protected getNodes<T extends CmsComponentNodeData>(type: string): T[] | null {
    return this.nodePageData.filter(node => node.type === type) as T[];
  }

  protected getNodeId(src: { id: string } | string): string {
    return BaseComponentResolver.getNodeId(src);
  }

  protected getMediaDataUrls(
    mediaNodeId: string = null,
    mediaNodeType: TMediaNodeType,
    componentNodeData: CmsComponentNodeData[] = null,
    includeMetas: boolean = false
  ): TImageConfig {
    if (!mediaNodeId) {
      return null;
    }

    return BaseComponentResolver.getImageConfig(mediaNodeId, mediaNodeType, componentNodeData ?? this.nodePageData, includeMetas);
  }

  protected getButtonType(node: ParagraphTextMediaBoxData | ParagraphCtaData = null): ButtonTypeEnum {
    if (!node?.attributes?.field_button_color) {
      return ButtonTypeEnum.WHITE;
    }

    switch (node.attributes.field_button_color.color?.toLowerCase()) {
      case SPS_COLORS.BLUE_DARK:
        return ButtonTypeEnum.PRIMARY_DARK;
      case SPS_COLORS.GRAY_LIGHT1:
        return ButtonTypeEnum.SECONDARY;
      default:
        return ButtonTypeEnum.PRIMARY;
    }
  }

  protected getBgColor(attributes: CmsComponentNodeDataAttributes): string | null {
    return attributes.field_bg_color_paragraph?.color || null;
  }

  protected getMargins(attributes: CmsComponentNodeDataAttributes): number[] {
    const { field_margin_top, field_margin_bottom } = attributes;
    return [field_margin_top, field_margin_bottom];
  }

  protected getAnchor(attributes: CmsComponentNodeDataAttributes): string | null {
    return attributes.field_anchor_id ?? null;
  }

  protected hyphenate(string: string): string {
    return BaseComponentResolver.hyphenate(string);
  }
}
